import React, { useMemo } from "react";
import { Box, Typography, useTheme, Chip, Grid } from "@mui/material";
import { FinanceType, FinanceTypeDef } from "../../../../models/finance/FinanceType";
import {
  ResponsiveContainer,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Area,
  Tooltip,
  ReferenceLine,
  Brush,
} from "recharts";
import { useCurrencyFormatter } from "../../../../contexts/CurrencyContext";
import { useUserProfileContext } from "../../../../contexts/ProfileContext";

interface MonthlyReportsPerDayChartProps {
  valuesPerDay: Map<Date, number>;
  type: FinanceTypeDef;
}

const MonthlyReportsPerDayChartComponent: React.FC<
  MonthlyReportsPerDayChartProps
> = ({ valuesPerDay, type }) => {
  const { formatCompactCurrencyNumber, formatCurrencyNumber } = useCurrencyFormatter();
  const { formatDate } = useUserProfileContext();
  const theme = useTheme();

  const chartData = useMemo(() => {
    const sortedEntries = Object.entries(valuesPerDay)
      .map(([dateStr, value]) => [new Date(dateStr), value] as [Date, number])
      .sort(([dateA], [dateB]) => dateA.getTime() - dateB.getTime());

    return sortedEntries.map(([date, value]) => ({
      date: formatDate(date),
      value: value,
      dateObj: date,
    }));
  }, [valuesPerDay, formatDate]);

  const stats = useMemo(() => {
    const values = chartData.map(d => d.value);
    const total = values.reduce((sum, val) => sum + val, 0);
    const average = values.length > 0 ? total / values.length : 0;
    const max = values.length > 0 ? Math.max(...values) : 0;
    const min = values.length > 0 ? Math.min(...values) : 0;
    const activeDays = values.filter(val => val > 0).length;
    
    return { total, average, max, min, activeDays };
  }, [chartData]);

  const isExpense = type === FinanceType.EXPENSE;
  const themeColor = isExpense ? theme.palette.error : theme.palette.success;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'white',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 2,
            boxShadow: theme.shadows[8],
            minWidth: 150,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: themeColor.main }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {formatCurrencyNumber(payload[0].value)}
          </Typography>
          {payload[0].value > stats.average && (
            <Chip 
              label="Above average" 
              size="small" 
              color={isExpense ? "error" : "success"}
              sx={{ mt: 1, fontSize: '0.7rem', height: 20 }}
            />
          )}
        </Box>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No daily data available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No {isExpense ? 'expenses' : 'income'} recorded for this period
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Summary Statistics */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, backgroundColor: themeColor.main + '08' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: themeColor.main }}>
                {formatCurrencyNumber(stats.average)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                Daily Average
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, backgroundColor: themeColor.main + '08' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: themeColor.main }}>
                {formatCurrencyNumber(stats.max)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                Highest Day
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, backgroundColor: themeColor.main + '08' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: themeColor.main }}>
                {stats.activeDays}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                Active Days
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, backgroundColor: themeColor.main + '08' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: themeColor.main }}>
                {((stats.activeDays / chartData.length) * 100).toFixed(0)}%
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                Activity Rate
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Enhanced Chart */}
      <Box sx={{ height: 350, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart 
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <defs>
              <linearGradient id={`gradient-${type}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={themeColor.main} stopOpacity={0.3} />
                <stop offset="95%" stopColor={themeColor.main} stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id={`strokeGradient-${type}`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={themeColor.light} />
                <stop offset="100%" stopColor={themeColor.main} />
              </linearGradient>
            </defs>

            <CartesianGrid 
              strokeDasharray="3 3" 
              vertical={false}
              stroke={theme.palette.grey[200]}
            />

            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
            />
            
            <YAxis 
              tickFormatter={(val) => formatCompactCurrencyNumber(val)}
              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              axisLine={false}
              tickLine={false}
              width={60}
            />

            {/* Average line */}
            <ReferenceLine 
              y={stats.average} 
              stroke={themeColor.dark} 
              strokeDasharray="5 5"
              strokeWidth={2}
            />

            <Tooltip content={<CustomTooltip />} />

            <Area
              type="monotone"
              dataKey="value"
              stroke={`url(#strokeGradient-${type})`}
              fill={`url(#gradient-${type})`}
              strokeWidth={3}
              dot={false}
              activeDot={{
                r: 6,
                fill: themeColor.main,
                stroke: "white",
                strokeWidth: 2,
              }}
            />

            {/* Brush for zooming */}
            {chartData.length > 15 && (
              <Brush
                dataKey="date"
                height={30}
                stroke={themeColor.main}
                fill={themeColor.light + '20'}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </Box>

      {/* Additional Insights */}
      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
        {stats.max > stats.average * 2 && (
          <Chip
            label={`Peak spending day: ${formatCurrencyNumber(stats.max)}`}
            size="small"
            variant="outlined"
            color={isExpense ? "error" : "success"}
          />
        )}
        {stats.activeDays < chartData.length * 0.3 && (
          <Chip
            label={`Low activity: ${stats.activeDays}/${chartData.length} days`}
            size="small"
            variant="outlined"
            color="warning"
          />
        )}
        {stats.activeDays > chartData.length * 0.8 && (
          <Chip
            label={`Highly active: ${stats.activeDays}/${chartData.length} days`}
            size="small"
            variant="outlined"
            color="info"
          />
        )}
      </Box>
    </Box>
  );
};

export default MonthlyReportsPerDayChartComponent;
